import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom';
import { Button, Dropdown, DropdownSearchResult, Groupper, Input, Message, Table } from 'react-frontier';
import { useBreadcrumbs } from '../Hooks';
import { UserClass } from '../SiteClasses';
import { TIMEZONE, inputToUnix, formatUnix } from '../SiteUtil';
import { MEXICO_STATES, MEXICO_VIEWBOX, projectCoords, stateBox, parseRings, insideRings } from '../components/MexicoStates';
import API, { StateEventRow } from '../API';

interface Municipality{
	id: number,
	name: string,
	d: string,
	rings: number[][][],
	box: { x: number, y: number, width: number, height: number },
	label: { x: number, y: number },
}

interface Camera{
	s: number,
	tx: number,
	ty: number,
	animate: boolean,
}

const MIN_SCALE = 1;
const MAX_SCALE = 400;
const municipalityCache = new Map<number, Promise<Municipality[]>>();

function heatColor(total: number, max: number){
	if(!total) return '#e9e9e9';
	var ratio = max > 0 ? total / max : 0;
	return `hsl(${Math.round(48 - ratio * 48)}, 90%, ${Math.round(78 - ratio * 38)}%)`;
}

function loadMunicipalities(state_id: number){
	if(!municipalityCache.has(state_id)){
		var req = fetch(`${process.env.PUBLIC_URL}/maps/municipios/${state_id}.json`).then(r=>r.json()).then((rows: { id: number, name: string, d: string }[])=>rows.map(m=>{
			var rings = parseRings(m.d);
			var xs = rings.flat().map(p=>p[0]), ys = rings.flat().map(p=>p[1]);
			var x = Math.min(...xs), y = Math.min(...ys);
			var main = stateBox(m.d);
			return {
				...m,
				rings,
				box: { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y },
				label: { x: main.x + main.width / 2, y: main.y + main.height / 2 },
			};
		}));
		req.catch(()=>municipalityCache.delete(state_id));
		municipalityCache.set(state_id, req);
	}
	return municipalityCache.get(state_id);
}

const STATE_RINGS = MEXICO_STATES.map(s=>({ state_id: s.state_id, state_name: s.state_name, rings: parseRings(s.d) }));

function stateFromCoords(lat: number, lon: number){
	var p = projectCoords(lat, lon);
	return STATE_RINGS.find(s=>insideRings(s.rings, p.x, p.y)) || null;
}

var StatesReport = ()=>{
	var { setTitle } = useBreadcrumbs();
	var [dateStart, setDateStart] = useState<string>(DateTime.now().setZone(TIMEZONE).startOf('month').toFormat('yyyy-MM-dd'));
	var [dateEnd, setDateEnd] = useState<string>(DateTime.now().setZone(TIMEZONE).endOf('month').toFormat('yyyy-MM-dd'));
	var [users, setUsers] = useState<UserClass[]>(null);
	var [userFilter, setUserFilter] = useState<number>(null);
	var [promoterFilter, setPromoterFilter] = useState<number>(null);
	var [events, setEvents] = useState<StateEventRow[]>(null);
	var [prompts, setPrompts] = useState<string[]>(null);
	var [selected, setSelected] = useState<number>(null);
	var [hovered, setHovered] = useState<string>(null);
	var [municipalities, setMunicipalities] = useState<Municipality[]>(null);
	var [camera, setCamera] = useState<Camera>({ s: 1, tx: 0, ty: 0, animate: true });
	var svgRef = useRef<SVGSVGElement>(null);
	var dragRef = useRef<{ x: number, y: number, tx: number, ty: number, moved: boolean }>(null);

	useEffect(()=>{
		setTitle('Mapa de eventos');
		API.getUsers(200, 0).then(res=>{ if(!res.error) setUsers(res.data || []); }).catch(()=>{});
	}, []);

	var boxes = useMemo(()=>new Map(MEXICO_STATES.map(s=>[s.state_id, stateBox(s.d)])), []);

	var fitCamera = (state_id: number): Camera=>{
		var b = state_id!=null && boxes.get(state_id);
		if(!b) return { s: 1, tx: 0, ty: 0, animate: true };
		var s = Math.min(1000 / b.width, 620 / b.height) * 0.85;
		return { s, tx: 500 - (b.x + b.width / 2) * s, ty: 310 - (b.y + b.height / 2) * s, animate: true };
	};

	useEffect(()=>{
		setCamera(fitCamera(selected));
		setMunicipalities(null);
		if(selected==null) return;
		var active = true;
		loadMunicipalities(selected).then(m=>{ if(active) setMunicipalities(m); }).catch(()=>{});
		return ()=>{ active = false; };
	}, [selected]);

	useEffect(()=>{
		var svg = svgRef.current;
		if(!svg) return;
		var onWheel = (ev: WheelEvent)=>{
			ev.preventDefault();
			var rect = svg.getBoundingClientRect();
			var px = (ev.clientX - rect.left) * 1000 / rect.width;
			var py = (ev.clientY - rect.top) * 620 / rect.height;
			setCamera(c=>{
				var s = Math.min(MAX_SCALE, Math.max(MIN_SCALE, c.s * Math.exp(-ev.deltaY * 0.0015)));
				return { s, tx: px - (px - c.tx) * s / c.s, ty: py - (py - c.ty) * s / c.s, animate: false };
			});
		};
		svg.addEventListener('wheel', onWheel, { passive: false });
		return ()=>svg.removeEventListener('wheel', onWheel);
	}, [events]);

	var zoomBy = (factor: number)=>{
		setCamera(c=>{
			var s = Math.min(MAX_SCALE, Math.max(MIN_SCALE, c.s * factor));
			return { s, tx: 500 - (500 - c.tx) * s / c.s, ty: 310 - (310 - c.ty) * s / c.s, animate: true };
		});
	};

	var onPointerDown = (ev: React.PointerEvent<SVGSVGElement>)=>{
		dragRef.current = { x: ev.clientX, y: ev.clientY, tx: camera.tx, ty: camera.ty, moved: false };
	};

	var onPointerMove = (ev: React.PointerEvent<SVGSVGElement>)=>{
		var drag = dragRef.current;
		if(!drag) return;
		var dx = ev.clientX - drag.x, dy = ev.clientY - drag.y;
		if(!drag.moved && Math.hypot(dx, dy) < 4) return;
		if(!drag.moved){
			drag.moved = true;
			ev.currentTarget.setPointerCapture(ev.pointerId);
		}
		var rect = ev.currentTarget.getBoundingClientRect();
		setCamera(c=>({ ...c, tx: drag.tx + dx * 1000 / rect.width, ty: drag.ty + dy * 620 / rect.height, animate: false }));
	};

	var onPointerUp = (ev: React.PointerEvent<SVGSVGElement>)=>{
		var drag = dragRef.current;
		if(drag?.moved){
			ev.currentTarget.releasePointerCapture(ev.pointerId);
			setTimeout(()=>{ dragRef.current = null; }, 0);
		}else{
			dragRef.current = null;
		}
	};

	var onClickCapture = (ev: React.MouseEvent)=>{
		if(dragRef.current?.moved) ev.stopPropagation();
	};

	var generate = (setLoading: (l: boolean)=>void)=>{
		setPrompts(null);
		var start = inputToUnix(dateStart);
		var end = inputToUnix(dateEnd);
		if(start===null || end===null) return setPrompts(['Selecciona fechas válidas.']);
		if(start > end) return setPrompts(['La fecha de inicio no puede ser mayor a la fecha de fin.']);

		setLoading(true);
		API.getStatesReport(start, end + 86399, promoterFilter ?? undefined, userFilter ?? undefined).then(res=>{
			if(res.error) return setPrompts([res.message]);
			setEvents((res.data || []).map(e=>{
				if(e.state_id!=null || e.lattitude==null || e.longitude==null) return e;
				var st = stateFromCoords(e.lattitude, e.longitude);
				return st ? { ...e, state_id: st.state_id, state_name: st.state_name } : e;
			}));
			setSelected(null);
			setCamera(fitCamera(null));
		}).catch(()=>{
			setPrompts(['Hubo un error generando el reporte. (LCL-RPTST-1)']);
		}).finally(()=>setLoading(false));
	};

	// Dates of the same event (same name, same state) stack into one row and count as 1
	var stacked = useMemo(()=>{
		var out = new Map<string, StateEventRow & { dates: StateEventRow[] }>();
		for(var e of events || []){
			var key = `${e.state_id}|${(e.event_name || '').trim().toLowerCase()}`;
			if(!out.has(key)) out.set(key, { ...e, tickets_sold: 0, dates: [] });
			var st = out.get(key);
			st.dates.push(e);
			st.tickets_sold += Number(e.tickets_sold) || 0;
		}
		return Array.from(out.values());
	}, [events]);

	var counts = useMemo(()=>{
		var out = new Map<number, number>();
		for(var e of stacked){
			if(e.state_id!=null) out.set(e.state_id, (out.get(e.state_id) || 0) + 1);
		}
		return out;
	}, [stacked]);

	var max = Math.max(0, ...Array.from(counts.values()));
	var stateName = (id: number)=> MEXICO_STATES.find(s=>s.state_id===id)?.state_name;
	var selectedEvents = useMemo(()=>selected==null ? [] : stacked.filter(e=>e.state_id===selected), [stacked, selected]);
	var ranking = Array.from(counts.entries()).sort((a, b)=> b[1] - a[1]);

	var spots = useMemo(()=>{
		var out = new Map<string, { x: number, y: number, total: number, names: Set<string> }>();
		for(var e of selectedEvents){
			if(e.lattitude==null || e.longitude==null) continue;
			var key = `${e.lattitude},${e.longitude}`;
			if(!out.has(key)) out.set(key, { ...projectCoords(e.lattitude, e.longitude), total: 0, names: new Set() });
			var spot = out.get(key);
			spot.total++;
			spot.names.add(e.venue_name || e.city || 'Sin recinto');
		}
		return Array.from(out.values());
	}, [selectedEvents]);
	var spotMax = Math.max(1, ...spots.map(s=>s.total));
	var noCoords = selectedEvents.filter(e=>e.lattitude==null || e.longitude==null).length;
	var unmatched = stacked.filter(e=>e.state_id==null).length;

	var municipalityCounts = useMemo(()=>{
		var out = new Map<number, number>();
		if(!municipalities) return out;
		for(var spot of spots){
			var found = municipalities.find(m=>spot.x >= m.box.x && spot.x <= m.box.x + m.box.width && spot.y >= m.box.y && spot.y <= m.box.y + m.box.height && insideRings(m.rings, spot.x, spot.y));
			if(found) out.set(found.id, (out.get(found.id) || 0) + spot.total);
		}
		return out;
	}, [municipalities, spots]);
	var municipalityMax = Math.max(0, ...Array.from(municipalityCounts.values()));

	var statePaths = useMemo(()=>MEXICO_STATES.map(s=>(
		<path
			key={`MXST-${s.state_id}`}
			d={s.d}
			fill={selected===s.state_id ? (municipalities ? 'none' : '#f4f4f4') : heatColor(counts.get(s.state_id) || 0, max)}
			opacity={selected!=null && selected!==s.state_id ? 0.35 : 1}
			stroke={selected===s.state_id ? (municipalities ? 'none' : '#000') : '#fff'}
			strokeWidth={selected===s.state_id ? 2 : 1}
			vectorEffect='non-scaling-stroke'
			style={{ cursor: 'pointer', transition: 'opacity 0.3s' }}
			onMouseEnter={()=>setHovered(`${s.state_name}: ${counts.get(s.state_id) || 0}`)}
			onClick={()=>setSelected(v=> v===s.state_id ? null : s.state_id)}
		>
			<title>{`${s.state_name}: ${counts.get(s.state_id) || 0}`}</title>
		</path>
	)), [selected, municipalities, counts, max]);

	var municipalityPaths = useMemo(()=>(municipalities || []).map(m=>(
		<path
			key={`MXMN-${m.id}`}
			d={m.d}
			fill={municipalityCounts.get(m.id) ? heatColor(municipalityCounts.get(m.id), municipalityMax) : '#f7f7f7'}
			stroke='#9a9a9a'
			strokeWidth={0.6}
			vectorEffect='non-scaling-stroke'
			onMouseEnter={()=>setHovered(`${m.name}: ${municipalityCounts.get(m.id) || 0}`)}
		>
			<title>{`${m.name}: ${municipalityCounts.get(m.id) || 0}`}</title>
		</path>
	)), [municipalities, municipalityCounts, municipalityMax]);

	var searchPromoters = async (query: string): Promise<DropdownSearchResult>=>{
		var res = await API.getPromoters({ search: query });
		if(res.error) return { error: res.message, items: [] };
		return { error: null, items: res.data.promoters.map(p=>({ value: String(p.promoter_id), text: p.promoter_name })) };
	};

	return <div style={{ marginTop: 20 }}>
		<Groupper title='Mapa de eventos' titleCentered actions={
			<Button text='Generar' color='black' onClick={generate} />
		} style={{ maxWidth: 500, margin: 'auto' }}>
			<div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
				<Input label='Fecha inicio' inputType='date' value={dateStart} onChange={setDateStart} style={{ margin: 0, flex: 1 }} />
				<Input label='Fecha fin' inputType='date' value={dateEnd} onChange={setDateEnd} style={{ margin: 0, flex: 1 }} />
			</div>
			<Dropdown
				label='Promotor'
				placeholder='Todos'
				value={promoterFilter!=null ? String(promoterFilter) : null}
				onAsyncSearch={searchPromoters}
				minSearchLength={1}
				searchTimeout={300}
				emptyText='Sin resultados'
				searchingText='Buscando...'
				onValueChange={v=>setPromoterFilter(v!=null && v!=='' ? parseInt(v) : null)}
				style={{ marginTop: 12, marginBottom: 0 }}
			/>
			<Dropdown
				search
				label='Comercial asignado'
				placeholder='Todos'
				value={userFilter!=null ? String(userFilter) : ''}
				items={[{ text: 'Todos', value: '' }, ...(users || []).map(u=>({ text: `${u.first_name} ${u.last_name}`, value: String(u.user_id) }))]}
				onValueChange={v=>setUserFilter(v!=null && v!=='' ? parseInt(v) : null)}
				style={{ marginTop: 12, marginBottom: 0 }}
			/>
			<Message type='error' list={prompts} style={{ marginTop: 12 }} />
		</Groupper>

		{!!events && (
			<Groupper title='Eventos por estado' titleCentered style={{ maxWidth: 900, margin: '20px auto 0' }}>
				<div style={{ textAlign: 'center', color: 'var(--color-gray-500, #777)', marginBottom: 12 }}>
					Eventos: <b>{stacked.length}</b>
				</div>
				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, minHeight: 32 }}>
					<div style={{ fontWeight: 600 }}>{hovered || ' '}</div>
					<div style={{ display: 'flex', gap: 4 }}>
						<Button icon iconName='plus' size='tiny' onClick={()=>zoomBy(1.5)} />
						<Button icon iconName='minus' size='tiny' onClick={()=>zoomBy(1 / 1.5)} />
						<Button icon iconName='expand' size='tiny' onClick={()=>setCamera(fitCamera(selected))} />
					</div>
				</div>
				<svg
					ref={svgRef}
					viewBox={MEXICO_VIEWBOX}
					style={{ width: '100%', height: 'auto', display: 'block', overflow: 'hidden', touchAction: 'none', cursor: 'grab',userSelect: 'none' }}
					onMouseLeave={()=>setHovered(null)}
					onPointerDown={onPointerDown}
					onPointerMove={onPointerMove}
					onPointerUp={onPointerUp}
					onPointerCancel={onPointerUp}
					onClickCapture={onClickCapture}
				>
					<defs>
						<radialGradient id='mx-heat-blob'>
							<stop offset='0' stopColor='#000' stopOpacity={1} />
							<stop offset='1' stopColor='#000' stopOpacity={0} />
						</radialGradient>
						<filter id='mx-heat' x='-50%' y='-50%' width='200%' height='200%' colorInterpolationFilters='sRGB'>
							<feColorMatrix type='matrix' values='0 0 0 1 0  0 0 0 1 0  0 0 0 1 0  0 0 0 1 0' />
							<feComponentTransfer>
								<feFuncR type='table' tableValues='1 1 1 0.95 0.7' />
								<feFuncG type='table' tableValues='0.9 0.85 0.55 0.25 0.05' />
								<feFuncB type='table' tableValues='0.3 0.2 0.1 0.1 0.05' />
								<feFuncA type='table' tableValues='0 0.55 0.75 0.85 0.9' />
							</feComponentTransfer>
						</filter>
					</defs>
					<g style={{ transform: `matrix(${camera.s}, 0, 0, ${camera.s}, ${camera.tx}, ${camera.ty})`, transition: camera.animate ? 'transform 0.5s ease' : 'none' }}>
						{statePaths}
						{selected!=null && municipalityPaths}
						{selected!=null && (
							<g filter='url(#mx-heat)' style={{ pointerEvents: 'none' }}>
								{spots.map(p=>(
									<circle
										key={`MXHT-${p.x}-${p.y}`}
										cx={p.x}
										cy={p.y}
										r={22 / camera.s}
										fill='url(#mx-heat-blob)'
										opacity={0.35 + 0.65 * p.total / spotMax}
									/>
								))}
							</g>
						)}
						{selected!=null && (
							<g style={{ pointerEvents: 'none' }}>
								{(municipalities || []).filter(m=>municipalityCounts.has(m.id) || Math.min(m.box.width, m.box.height) * camera.s >= 45).map(m=>(
									<text
										key={`MXLB-${m.id}`}
										x={m.label.x}
										y={m.label.y}
										fontSize={(municipalityCounts.has(m.id) ? 12 : 10) / camera.s}
										fontWeight={municipalityCounts.has(m.id) ? 700 : 400}
										textAnchor='middle'
										dominantBaseline='middle'
										fill={municipalityCounts.has(m.id) ? '#111' : '#555'}
										stroke='#fff'
										strokeWidth={3 / camera.s}
										paintOrder='stroke'
									>
										{municipalityCounts.has(m.id) ? `${m.name} (${municipalityCounts.get(m.id)})` : m.name}
									</text>
								))}
							</g>
						)}
					</g>
				</svg>
				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 12, color: 'var(--color-gray-500, #777)', marginTop: 8 }}>
					0
					<div style={{ width: 160, height: 10, borderRadius: 5, background: `linear-gradient(to right, ${heatColor(1, 100)}, ${heatColor(50, 100)}, ${heatColor(100, 100)})` }} />
					{selected!=null ? municipalityMax : max}
					{selected==null && unmatched > 0 && <span style={{ marginLeft: 12 }}>Sin estado: <b>{unmatched}</b></span>}
					{selected!=null && noCoords > 0 && <span style={{ marginLeft: 12 }}>Sin ubicación en el mapa: <b>{noCoords}</b></span>}
				</div>

				{selected==null ? (
					ranking.length > 0 && <div style={{ overflowX: 'auto', marginTop: 20 }}>
						<Table fitted striped>
							<Table.Head>
								<Table.Row>
									<Table.Cell header>Estado</Table.Cell>
									<Table.Cell header collapsing centered>Eventos</Table.Cell>
								</Table.Row>
							</Table.Head>
							<Table.Body>
								{ranking.map(([id, total])=>(
									<Table.Row key={`MXRK-${id}`} selectable onClick={()=>setSelected(id)}>
										<Table.Cell>{stateName(id)}</Table.Cell>
										<Table.Cell collapsing centered>{total}</Table.Cell>
									</Table.Row>
								))}
							</Table.Body>
						</Table>
					</div>
				) : (
					<div style={{ overflowX: 'auto', marginTop: 20 }}>
						<Table fitted striped>
							<Table.Head title={`${stateName(selected)} (${selectedEvents.length})`} titleRight={<Button size='tiny' text='Ver todos' onClick={()=>setSelected(null)} />}>
								<Table.Row>
									<Table.Cell header>Evento</Table.Cell>
									<Table.Cell header>Promotor</Table.Cell>
									<Table.Cell header>Comercial</Table.Cell>
									<Table.Cell header>Recinto</Table.Cell>
									<Table.Cell header collapsing>Fecha</Table.Cell>
									<Table.Cell header collapsing centered>Boletos</Table.Cell>
								</Table.Row>
							</Table.Head>
							<Table.Body>
								{selectedEvents.length===0 ? (
									<Table.Row>
										<Table.Cell colSpan={6} centered>Sin eventos</Table.Cell>
									</Table.Row>
								) : selectedEvents.map(e=>(
									<Table.Row key={`MXEV-${e.date_id}`}>
										<Table.Cell>{e.event_name}</Table.Cell>
										<Table.Cell><Link to={`/promoters/${e.promoter_id}/events`}>{e.promoter_name}</Link></Table.Cell>
										<Table.Cell>{e.user_name || 'Sin asignar'}</Table.Cell>
										<Table.Cell>{[e.venue_name, e.city].filter(Boolean).join(', ')}</Table.Cell>
										<Table.Cell collapsing>{formatUnix(e.date)}{e.dates.length > 1 && ` (+${e.dates.length - 1})`}</Table.Cell>
										<Table.Cell collapsing centered>{e.tickets_sold}</Table.Cell>
									</Table.Row>
								))}
							</Table.Body>
						</Table>
					</div>
				)}
			</Groupper>
		)}
	</div>;
}

export default StatesReport;
